// StepTwo.jsx (namespaced to step_two.locations)
import React from "react";
import {
  Box,
  Paper,
  Grid,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Stack,
  InputAdornment,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
// import { ReactComponent as DeleteOutlineIcon } from "../../../../assets/DeleteSvg.svg";
import { ReactComponent as DeleteOutlineIcon } from "../../../../assets/DeleteSvg.svg";
import Add from "@mui/icons-material/Add";
import {
  useFormContext,
  Controller,
  useFieldArray,
  useWatch,
} from "react-hook-form";
import { ReactComponent as DeltaTechSvg } from "../../../../assets/DeltaTech.svg";

const COUNTRIES = ["Deutschland", "Österreich", "Schweiz"];

// default builders
const makeDefaultBereich = () => ({ name: "" });
const makeDefaultLocation = () => ({
  name: "",
  sameAsInvoice: false,
  company: "",
  addressLine2: "",
  street: "",
  country: COUNTRIES[0],
  zip: "",
  city: "",
  email: "",
  // Bereiche are OPTIONAL; start with none
  bereiche: [],
});

export default function StepTwo() {
  const { control, setValue, getValues } = useFormContext();

  // locations field array (NAMESPACED)
  const { fields, append, remove } = useFieldArray({
    control,
    name: "step_two.locations",
  });

  // which accordion is open
  const [expanded, setExpanded] = React.useState(0);

  // ensure at least one empty expanded location initially
  React.useEffect(() => {
    if (fields.length === 0) {
      append(makeDefaultLocation());
      setExpanded(0);
    }
  }, []);

  // watch the currently expanded item for validation gating
  const current = useWatch({ control, name: `step_two.locations.${expanded}` });

  // validity for gating the Add button
  const isCurrentValid = React.useMemo(() => {
    if (!current) return false;

    const { name, sameAsInvoice, bereiche = [] } = current;

    if (!name?.trim()) return false;

    if (!sameAsInvoice) {
      const req = ["company", "street", "country", "zip", "city", "email"];
      for (const k of req) {
        const v = (current[k] ?? "").toString().trim();
        if (!v) return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(current.email)) return false;
    }

    if (bereiche.length > 0 && !bereiche.every((b) => (b.name ?? "").trim())) {
      return false;
    }

    return true;
  }, [current]);

  const handleAddLocation = () => {
    const nextIndex = fields.length;
    append(makeDefaultLocation());
    setExpanded(nextIndex);
  };

  // copies invoice → location fields
  const copyFromInvoice = (idx) => {
    const inv = getValues("step_one.invoice") || {};
    const patch = {
      company: inv.company || "",
      addressLine2: inv.addressLine2 || "",
      street: inv.address || inv.street || "",
      country: inv.country || COUNTRIES[0],
      zip: inv.zip || "",
      city: inv.city || "",
      email: inv.email || "",
    };
    Object.entries(patch).forEach(([k, v]) => {
      setValue(`step_two.locations.${idx}.${k}`, v, {
        shouldDirty: true,
        shouldValidate: true,
      });
    });
  };

  return (
    <Box className="step2">
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Stack sx={{ mb: 2 }}>
          <Typography className="textSecondary" sx={{ fontWeight: 500, mb: 2 }}>
            <DeltaTechSvg /> {getValues("step_one.customerName") || "Company Name"}
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 500, color: "var(--color-primary)" }}>
            Standorte
          </Typography>
          <Typography className="textSecondary" sx={{ fontWeight: 500 }}>
            Ein Kunde kann beliebig viele Standorte haben.
          </Typography>
        </Stack>

        {fields.map((field, idx) => (
          <Accordion
            key={field.id}
            expanded={expanded === idx}
            onChange={(_, open) => setExpanded(open ? idx : -1)}
            disableGutters
            sx={{
              mb: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%" }}
              >
                <Typography sx={{ fontWeight: 500, color: "var(--color-primary)" }}>
                  {getValues(`step_two.locations.${idx}.name`)?.trim() || "Neuer Standort"}
                </Typography>

                <IconButton
                  aria-label="Entfernen"
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(idx);
                    if (fields.length - 1 <= 1) setExpanded(0);
                    else setExpanded((prev) => Math.max(0, prev - 1));
                  }}
                >
                  <DeleteOutlineIcon color="error" />
                </IconButton>
              </Stack>
            </AccordionSummary>

            <AccordionDetails sx={{ paddingRight: "20%" }}>
              {/* SECTION 1: Standortname + checkbox */}
              <Grid container spacing={3}>
                <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center" }}>
                  <Controller
                    name={`step_two.locations.${idx}.name`}
                    control={control}
                    rules={{ required: "Pflichtfeld" }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Standortname"
                        placeholder='z. B.: "Werk Neckarsulm"'
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message || 'z. B.: "Werk Neckarsulm"'}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center" }}>
                  <Controller
                    name={`step_two.locations.${idx}.sameAsInvoice`}
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={!!field.value}
                            onChange={(_, checked) => {
                              // set checkbox value
                              field.onChange(checked);
                              // when checked, copy invoice details into this location
                              if (checked) {
                                copyFromInvoice(idx);
                              }
                            }}
                          />
                        }
                        label="Anschrift wie Rechnungsadresse"
                      />
                    )}
                  />
                </Grid>
              </Grid>

              {/* Address section (auto-hidden when sameAsInvoice is checked) */}
              <AddressSection control={control} idx={idx} />

              <Divider sx={{ my: 2 }} />

              {/* SECTION 2: Bereiche (optional; two per row) */}
              <BereicheSection control={control} idx={idx} />
            </AccordionDetails>
          </Accordion>
        ))}

        <br />
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={handleAddLocation}
          disabled={fields.length > 0 && !isCurrentValid}
        >
          Standort hinzufügen
        </Button>
      </Paper>
    </Box>
  );
}

/* ---------- Address section (hidden if sameAsInvoice) ---------- */
function AddressSection({ control, idx }) {
  const sameAsInvoice = useWatch({
    control,
    name: `step_two.locations.${idx}.sameAsInvoice`,
  });

  if (sameAsInvoice) return null;

  return (
    <Grid container spacing={4} sx={{ mt: 1 }}>
      <Grid item xs={12} md={6}>
        <Controller
          name={`step_two.locations.${idx}.company`}
          control={control}
          rules={{ required: "Pflichtfeld" }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              label="Unternehmen"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Controller
          name={`step_two.locations.${idx}.addressLine2`}
          control={control}
          render={({ field }) => <TextField {...field} fullWidth label="Adresszusatz (optional)" />}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Controller
          name={`step_two.locations.${idx}.street`}
          control={control}
          rules={{ required: "Pflichtfeld" }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              label="Straße und Hausnummer"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Controller
          name={`step_two.locations.${idx}.country`}
        control={control}
          rules={{ required: "Pflichtfeld" }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              select
              fullWidth
              label="Land"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            >
              {COUNTRIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <Controller
          name={`step_two.locations.${idx}.zip`}
          control={control}
          rules={{
            required: "Pflichtfeld",
            pattern: {
              value: /^[0-9A-Za-z -]{3,10}$/,
              message: "Ungültige PLZ",
            },
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              label="PLZ"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <Controller
          name={`step_two.locations.${idx}.city`}
          control={control}
          rules={{ required: "Pflichtfeld" }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              label="Ort"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Controller
          name={`step_two.locations.${idx}.email`}
          control={control}
          rules={{
            required: "Pflichtfeld",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Ungültige E-Mail-Adresse",
            },
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              label="E-Mail-Adresse"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Grid>
    </Grid>
  );
}

/* --------------------------- Bereiche (optional, 2 per row) --------------------------- */
function BereicheSection({ control, idx }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `step_two.locations.${idx}.bereiche`,
  });

  return (
    <Box>
      <Typography sx={{ fontWeight: 500, mb: 0.5, mt: 2, color: "var(--color-primary)" }}>
        Bereiche
      </Typography>
      <Typography className="textSecondary" sx={{ mb: 3 }}>
        Bereiche sind optional. Wenn du welche hinzufügst, benenne sie bitte.
      </Typography>

      <Grid container spacing={4}>
        {fields.map((field, bIndex) => (
          <Grid item xs={12} md={6} key={field.id}>
            <Controller
              name={`step_two.locations.${idx}.bereiche.${bIndex}.name`}
              control={control}
              rules={{ required: "Pflichtfeld" }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Bereichsname"
                  placeholder='z. B.: "Metallfertigung"'
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message || 'z. B.: "Metallfertigung"'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="Bereich entfernen"
                          onClick={() => remove(bIndex)}
                          edge="end"
                        >
                          <DeleteOutlineIcon color="error" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        ))}
      </Grid>

      <Button
        size="small"
        variant="text"
        startIcon={<Add />}
        onClick={() => append(makeDefaultBereich())}
        sx={{ mt: 1 }}
      >
        Bereich hinzufügen
      </Button>
    </Box>
  );
}
